export const EMBRACE_EXPLORATIONS_HTML = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Embrace Map · Round 4 — Editorial refinement &amp; imagery integration</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Inter:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap" rel="stylesheet">
<style>
  :root {
    --ivory:   #F7F5EE;
    --paper:   #F0EDE4;
    --paper-2: #E9E4D8;
    --card:    #FCFBF7;
    --ink:     #131211;
    --stone:   #6C645A;
    --stone-2: #948B7F;
    --stone-3: #B9B0A2;
    --navy:    #14264F;
    --navy-2:  #1E3768;
    --navy-soft:rgba(20,38,79,0.07);
    --navy-hair:rgba(20,38,79,0.24);
    --brass:   #96682F;
    --brass-2: #B78447;
    --brass-soft: rgba(150,104,47,0.14);
    --hair:    #DCD5C6;
    --hair-2:  #C9C1B0;

    /* Textural placeholders — editorial "atmosphere" tiles */
    --tex-boardroom: radial-gradient(120% 80% at 30% 20%, rgba(255,247,225,.28), transparent 45%),
                     radial-gradient(90% 60% at 80% 90%, rgba(20,38,79,.35), transparent 55%),
                     linear-gradient(140deg, #1E2A3E 0%, #33231A 55%, #3F2B1C 100%);
    --tex-paper:     radial-gradient(90% 70% at 20% 30%, rgba(255,245,220,.4), transparent 55%),
                     linear-gradient(160deg, #EDE3CE 0%, #DCC9A6 60%, #B79167 100%);
    --tex-ink:       radial-gradient(70% 60% at 30% 40%, rgba(180,140,80,.35), transparent 60%),
                     linear-gradient(140deg, #0F1B34 0%, #14264F 55%, #1E2A3E 100%);
    --tex-marble:    linear-gradient(135deg, #F0E9D5 0%, #DAC8A2 100%),
                     radial-gradient(50% 40% at 50% 50%, rgba(150,104,47,.15), transparent 70%);
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: var(--ivory); color: var(--ink); font-family: 'Inter', -apple-system, system-ui, sans-serif; -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
  /* Typography classes */
  .display   { font-family: 'Playfair Display', 'Merriweather', Georgia, serif; letter-spacing: -0.015em; }
  .serif     { font-family: 'Cormorant Garamond', 'Merriweather', Georgia, serif; }
  .body-serif{ font-family: 'Merriweather', Georgia, serif; }
  .mono      { font-family: 'IBM Plex Mono', monospace; }

  .eyebrow   { font-family: 'IBM Plex Mono', monospace; text-transform: uppercase; letter-spacing: 0.24em; font-size: 10px; color: var(--stone); }
  .eyebrow-navy  { color: var(--navy); }
  .eyebrow-brass { color: var(--brass); }
  .rubric {
    font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--brass); font-weight: 500;
  }
  .rubric::after { content: ''; display: inline-block; width: 40px; height: 1px; background: var(--brass); vertical-align: middle; margin-left: 12px; margin-right: 12px; }
  .rubric.only::after { display: none; }
  code { background: var(--paper); padding: 1px 5px; font-family: 'IBM Plex Mono', monospace; font-size: 11.5px; color: var(--ink); }

  .wrap { max-width: 1440px; margin: 0 auto; padding: 64px 44px 96px; }

  /* Editorial hero — this DOC's own cover, to demonstrate the direction */
  .cover { border-bottom: 1px solid var(--hair); padding-bottom: 56px; margin-bottom: 72px; }
  .cover .kicker { font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--brass); }
  .cover h1 {
    font-family: 'Playfair Display', serif; font-weight: 900;
    font-size: 68px; line-height: 0.98; letter-spacing: -0.02em;
    margin: 22px 0 22px; max-width: 1080px; color: var(--ink);
  }
  .cover .lede {
    font-family: 'Merriweather', Georgia, serif; font-size: 19px; line-height: 1.65;
    color: var(--stone); max-width: 800px;
  }
  .cover .byline {
    margin-top: 26px;
    display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone);
    padding-top: 20px; border-top: 1px solid var(--hair);
  }
  .cover .byline .sep { color: var(--hair-2); }
  .toc { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 28px; }
  .toc a { text-decoration: none; padding: 7px 14px; border: 1px solid var(--hair-2); font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--stone); transition: all .15s; }
  .toc a:hover { border-color: var(--navy); color: var(--navy); background: var(--card); }

  /* Section rhythm */
  .section { margin: 104px 0 0; }
  .section-head { margin-bottom: 32px; padding-bottom: 20px; border-bottom: 1px solid var(--hair); }
  .section-head .num {
    font-family: 'Playfair Display', serif; font-size: 20px; color: var(--brass); font-weight: 700;
    letter-spacing: 0.02em;
  }
  .section-head h2 {
    font-family: 'Playfair Display', serif; font-weight: 700;
    font-size: 40px; margin: 8px 0 12px; line-height: 1.08; letter-spacing: -0.015em; max-width: 940px;
  }
  .section-head .sub {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 16px; line-height: 1.65;
    color: var(--stone); max-width: 820px;
  }

  /* Editorial mockup stage — the "front page" treatment */
  .stage {
    background: var(--card);
    border: 1px solid var(--hair);
    box-shadow: 0 1px 0 var(--navy-hair), 0 20px 40px -30px rgba(20,20,20,0.15);
    position: relative;
    overflow: hidden;
  }
  .stage-caption {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--stone); padding: 10px 24px; border-bottom: 1px solid var(--hair); background: var(--paper);
    display: flex; justify-content: space-between; align-items: center;
  }
  .stage-caption .r { color: var(--brass); }

  /* ─────── Editorial masthead of the Embrace tab ─────── */
  .masthead {
    padding: 30px 64px 26px;
    display: grid; grid-template-columns: 1fr auto; gap: 24px; align-items: center;
    border-bottom: 1px solid var(--ink);
  }
  .masthead .brand {
    font-family: 'Playfair Display', serif; font-weight: 700; font-size: 24px;
    letter-spacing: -0.005em; color: var(--ink);
  }
  .masthead .brand em {
    font-style: italic; color: var(--brass); font-weight: 400;
  }
  .masthead .brand-sub {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.32em; text-transform: uppercase;
    color: var(--stone); margin-top: 4px;
  }
  .masthead .dateline {
    text-align: right;
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase;
    color: var(--stone); line-height: 1.7;
  }
  .masthead .dateline .cur { color: var(--brass); }
  /* Hairline "gazette rule" underneath */
  .rule-double { height: 4px; border-top: 1px solid var(--ink); border-bottom: 1px solid var(--ink); }

  /* ─────── Hero image band + client monogram ─────── */
  .cover-band {
    display: grid; grid-template-columns: 240px 1fr; gap: 0; align-items: stretch;
    border-bottom: 1px solid var(--hair);
  }
  .client-card {
    background: var(--paper); padding: 32px 28px;
    display: flex; flex-direction: column; justify-content: space-between;
    border-right: 1px solid var(--hair);
  }
  .client-card .k { font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--stone); }
  .client-card .monogram {
    font-family: 'Playfair Display', serif; font-weight: 900; font-size: 72px; line-height: 1;
    color: var(--navy); letter-spacing: -0.02em; margin: 12px 0 0;
  }
  .client-card .name {
    font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; margin-top: 20px; line-height: 1.15;
    color: var(--ink);
  }
  .client-card .meta {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); line-height: 1.7; margin-top: 12px;
  }
  .atmosphere {
    background: var(--tex-boardroom);
    background-size: cover; background-position: center; background-blend-mode: normal;
    min-height: 240px; position: relative;
  }
  .atmosphere::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(19,18,17,0.75) 100%);
  }
  .atmosphere .cap {
    position: absolute; left: 24px; right: 24px; bottom: 18px;
    color: var(--card); z-index: 2;
    display: flex; align-items: baseline; justify-content: space-between; gap: 20px; flex-wrap: wrap;
  }
  .atmosphere .cap .title {
    font-family: 'Playfair Display', serif; font-style: italic; font-size: 22px; font-weight: 400;
    line-height: 1.25; max-width: 620px; text-shadow: 0 2px 24px rgba(0,0,0,.5);
  }
  .atmosphere .cap .credit {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase;
    color: rgba(252, 251, 247, 0.7);
  }

  /* ─────── The Method map — editorial "story index" ─────── */
  .method-plate {
    padding: 44px 64px 24px; background: var(--card);
    border-bottom: 1px solid var(--hair);
  }
  .method-plate .rule-head {
    display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 24px;
  }
  .method-plate .rule-head .k {
    font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--brass);
  }
  .method-plate .rule-head .k::after { content: ''; display: inline-block; width: 30px; height: 1px; background: var(--brass); vertical-align: middle; margin-left: 10px; }
  .method-plate .rule-head .hint {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 13px; color: var(--stone);
  }
  .method-plate .fw-top, .method-plate .fw-bot {
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.32em; text-transform: uppercase;
    color: var(--stone); text-align: center; padding: 6px 0;
    position: relative;
  }
  .method-plate .fw-top::before, .method-plate .fw-top::after,
  .method-plate .fw-bot::before, .method-plate .fw-bot::after {
    content: ''; position: absolute; top: 50%; width: 24%; height: 1px; background: var(--hair-2);
  }
  .method-plate .fw-top::before, .method-plate .fw-bot::before { left: 12%; }
  .method-plate .fw-top::after, .method-plate .fw-bot::after { right: 12%; }
  .method-plate .fw-active {
    color: var(--brass); position: relative;
  }
  .method-plate .fw-recede { opacity: .40; }
  .method-plate .movements {
    display: grid; grid-template-columns: repeat(4, 1fr); text-align: center; padding: 22px 0 12px;
  }
  .method-plate .m { padding: 44px 8px 4px; cursor: pointer; position: relative; transition: opacity .3s ease; }
  .method-plate .m::before {
    content: ''; position: absolute; top: 22px; left: 50%; margin-left: -6px;
    width: 12px; height: 12px; background: var(--card); border: 1.5px solid var(--stone-2); border-radius: 50%;
    transition: all .3s ease;
  }
  .method-plate .m.current::before { background: var(--brass); border-color: var(--brass); }
  .method-plate .m.selected::before {
    background: var(--navy); border-color: var(--navy);
    box-shadow: 0 0 0 6px rgba(20,38,79,.12);
    width: 14px; height: 14px; margin-left: -7px; top: 21px;
  }
  .method-plate .m.recede { opacity: .30; }
  .method-plate .m .roman {
    font-family: 'Playfair Display', serif; font-style: italic; font-size: 13px;
    color: var(--brass); font-weight: 400; letter-spacing: 0.05em;
  }
  .method-plate .m .name {
    font-family: 'Playfair Display', serif; font-weight: 700; font-size: 26px;
    letter-spacing: -0.008em; margin-top: 3px;
  }
  .method-plate .m .fw {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--stone); margin-top: 5px;
  }
  .method-plate .m .intent {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 12.5px;
    color: var(--stone); margin-top: 12px; line-height: 1.55; padding: 0 8px;
  }
  .method-plate.compact .m { padding: 30px 8px 4px; }
  .method-plate.compact .m::before { top: 12px; }
  .method-plate.compact .m .name { font-size: 17px; }
  .method-plate.compact .m .roman { font-size: 11px; }
  .method-plate.compact .m .intent { display: none; }
  .method-plate .arcs {
    display: block; width: 100%; height: 56px; margin: 6px 0;
  }
  .method-plate.compact .arcs { height: 32px; }
  .idle-bar {
    padding: 16px 64px 30px; display: flex; justify-content: space-between; align-items: baseline; gap: 20px; flex-wrap: wrap;
    background: var(--card);
  }
  .idle-bar .l { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); }
  .idle-bar .l .b { color: var(--brass); }
  .idle-bar .r { font-family: 'Merriweather', serif; font-style: italic; font-size: 13px; color: var(--stone); }

  /* ─────── Selected · Canvas ─────── */
  .canvas {
    padding: 56px 72px 60px;
    background: linear-gradient(180deg, var(--card) 0%, var(--card) 100%);
    border-top: 1px solid var(--hair);
  }
  .canvas > * + * { margin-top: 48px; }

  /* Context ribbon — masthead-style breadcrumb */
  .ribbon {
    display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap;
    padding: 12px 20px; border-top: 1px solid var(--navy); border-bottom: 1px solid var(--navy);
    background: var(--paper);
    margin-bottom: 40px !important;
  }
  .ribbon .trail { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone); }
  .ribbon .trail .sep { margin: 0 10px; color: var(--hair-2); }
  .ribbon .trail .cur { color: var(--navy); font-weight: 500; }
  .ribbon .return {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
    background: var(--navy); border: none; color: var(--card); padding: 7px 14px; cursor: pointer;
  }

  /* Headline area — full editorial spread */
  .headline-block {
    display: grid; grid-template-columns: 1fr 300px; gap: 60px; align-items: start;
  }
  .headline-block .h-main .rubric-line {
    display: flex; align-items: center; gap: 14px;
  }
  .headline-block .h-main .rubric-line .r {
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--brass);
  }
  .headline-block .h-main .rubric-line .r-sub {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone);
  }
  .headline-block h3.headline {
    font-family: 'Playfair Display', serif; font-weight: 700;
    font-size: 52px; line-height: 1.05; letter-spacing: -0.015em; margin: 18px 0 20px;
    color: var(--ink); max-width: 780px;
  }
  .headline-block .deck {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 20px; line-height: 1.55;
    color: var(--stone); max-width: 720px; padding-left: 20px; border-left: 2px solid var(--brass-soft);
  }
  .headline-block .h-side {
    /* Right column · atmosphere image + "on the record" byline */
  }
  .headline-block .h-side .imagery {
    background: var(--tex-paper);
    background-size: cover; background-position: center;
    height: 280px; position: relative; border-bottom: 1px solid var(--hair);
  }
  .headline-block .h-side .imagery::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(19,18,17,0.35) 100%);
  }
  .headline-block .h-side .caption {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); margin-top: 10px; line-height: 1.65;
  }
  .headline-block .h-side .caption .b { color: var(--ink); }

  /* Byline strip under headline */
  .byline-strip {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    padding: 14px 0 0 20px; border-left: 2px solid transparent;
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone);
  }
  .byline-strip .avatar {
    width: 26px; height: 26px; border-radius: 50%;
    background: var(--tex-paper); background-size: cover;
    border: 1.5px solid var(--card); box-shadow: 0 0 0 1px var(--hair-2);
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Playfair Display', serif; font-size: 11px; color: var(--navy); font-weight: 700;
  }
  .byline-strip .b { color: var(--ink); }
  .byline-strip .sep { color: var(--hair-2); }

  /* Two-column article — Current Reflection & Working Journal facing */
  .article-grid {
    display: grid; grid-template-columns: 1.15fr 1fr; gap: 72px; align-items: start;
    padding-top: 50px; border-top: 3px double var(--hair-2);
  }
  .article-grid .col.cr .col-head {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 12px; margin-bottom: 22px; border-bottom: 1px solid var(--navy);
  }
  .article-grid .col.wj .col-head {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 12px; margin-bottom: 22px; border-bottom: 1px solid var(--brass);
  }
  .article-grid .col-head .k {
    font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 500;
  }
  .article-grid .col.cr .col-head .k { color: var(--navy); }
  .article-grid .col.wj .col-head .k { color: var(--brass); }
  .article-grid .col-head .m {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 12px; color: var(--stone);
  }

  /* Current Reflection composition — long-form article style */
  .cr-body {
    max-width: 640px;
  }
  .cr-body .lead {
    font-family: 'Merriweather', serif; font-size: 18px; line-height: 1.7; color: var(--ink);
    margin: 0;
  }
  .cr-body .lead::first-letter {
    font-family: 'Playfair Display', serif; font-size: 62px; font-weight: 900; line-height: 0.9;
    float: left; margin: 6px 12px 0 0; color: var(--navy);
  }
  .cr-body .para {
    font-family: 'Merriweather', serif; font-size: 15.5px; line-height: 1.75; color: var(--ink);
    margin-top: 20px;
  }
  .cr-body .plate {
    margin: 26px 0 8px; background: var(--tex-marble); background-size: cover;
    height: 160px; position: relative;
    border: 1px solid var(--hair);
  }
  .cr-body .plate::after {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(80% 60% at 30% 40%, rgba(20,38,79,0.05), transparent 70%);
  }
  .cr-body .plate-cap {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); margin-top: 10px; padding-left: 12px; border-left: 2px solid var(--brass);
  }
  .cr-actions {
    margin-top: 32px; display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
    padding-top: 22px; border-top: 1px solid var(--hair);
  }
  .cr-actions .btn-pill {
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--card); background: var(--navy); padding: 12px 22px; border: none; border-radius: 999px; cursor: pointer;
  }
  .cr-actions .link {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); border-bottom: 1px solid transparent; padding-bottom: 3px; cursor: pointer;
  }
  .cr-actions .link:hover { color: var(--navy); border-bottom-color: var(--navy); }

  /* Working Journal — fluid editorial notebook style */
  .wj-jot {
    padding: 16px 0 16px 22px; border-left: 1px solid var(--hair-2); position: relative;
    margin-bottom: 8px;
  }
  .wj-jot::before {
    content: ''; position: absolute; left: -1px; top: 16px; width: 3px; height: 32px; background: var(--brass);
  }
  .wj-jot .jot-body {
    font-family: 'Cormorant Garamond', 'Merriweather', serif; font-style: italic; font-weight: 500;
    font-size: 20px; line-height: 1.5; color: var(--ink);
  }
  .wj-controls {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    padding: 14px 0 0 22px;
    border-top: 1px dashed var(--hair-2); margin-top: 4px;
  }
  .vis-chip {
    display: inline-flex; align-items: center; gap: 6px;
    font-family: 'IBM Plex Mono', monospace; font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); padding: 3px 8px 3px 6px; border: 1px solid var(--hair-2); background: var(--card);
  }
  .vis-chip .dot { width: 6px; height: 6px; border-radius: 50%; }
  .vis-chip.private .dot { background: var(--ink); }
  .vis-chip.team .dot    { background: var(--stone); }
  .vis-chip.firm .dot    { background: var(--brass); }
  .btn-ghost-brass {
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase;
    color: var(--brass); background: none; border: none; padding: 6px 0;
    border-bottom: 1px solid var(--brass); cursor: pointer;
  }
  .btn-ghost-brass:hover { color: var(--ink); border-bottom-color: var(--ink); }
  .link-stone {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone); border: none; background: none; padding: 6px 0; border-bottom: 1px solid transparent; cursor: pointer;
  }
  .link-stone:hover { color: var(--navy); border-bottom-color: var(--navy); }

  /* Prior journal entries — clippings stream */
  .wj-entries { margin-top: 34px; }
  .wj-entry {
    padding: 18px 0 18px 22px; border-left: 1px solid var(--hair-2); position: relative;
  }
  .wj-entry .meta {
    display: flex; gap: 10px; flex-wrap: wrap; align-items: center;
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase;
    color: var(--stone);
  }
  .wj-entry .avatar {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--tex-paper); background-size: cover;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Playfair Display', serif; font-size: 9px; color: var(--navy); font-weight: 700;
    box-shadow: 0 0 0 1px var(--hair-2);
  }
  .wj-entry .body-txt {
    font-family: 'Cormorant Garamond', 'Merriweather', serif; font-style: italic; font-weight: 500;
    font-size: 17px; line-height: 1.55; color: var(--ink); margin-top: 8px;
  }
  .wj-entry.promoted { opacity: .45; }
  .wj-entry .row-mini { display: flex; gap: 14px; margin-top: 8px; }
  .wj-entry .row-mini button {
    font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase;
    background: none; border: none; color: var(--brass); padding: 2px 0; border-bottom: 1px solid transparent; cursor: pointer;
  }
  .wj-entry .row-mini button:hover { border-bottom-color: var(--brass); }
  .wj-entry .row-mini button.text { color: var(--stone); }
  .wj-entry .row-mini button.text:hover { color: var(--navy); border-bottom-color: var(--navy); }

  /* Prompts section — editorial index */
  .prompts-block {
    padding-top: 50px; border-top: 3px double var(--hair-2); display: grid; grid-template-columns: 240px 1fr; gap: 60px;
  }
  .prompts-block .lbl-col {
    font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 700; letter-spacing: -0.008em; line-height: 1.15;
  }
  .prompts-block .lbl-col .k {
    font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--brass); font-weight: 500;
    display: block; margin-bottom: 6px;
  }
  .prompts-block .lbl-col .depth {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 12.5px; line-height: 1.65; color: var(--stone); margin-top: 16px; font-weight: 400;
  }
  .prompt-index .row {
    display: grid; grid-template-columns: 40px 1fr auto;
    gap: 20px; padding: 18px 0; border-bottom: 1px solid var(--hair);
    align-items: baseline; cursor: pointer;
  }
  .prompt-index .row.open { border-bottom-color: var(--navy); background: linear-gradient(90deg, rgba(20,38,79,.03), transparent 70%); padding-left: 12px; margin-left: -12px; padding-right: 12px; margin-right: -12px; }
  .prompt-index .row .roman { font-family: 'Playfair Display', serif; font-style: italic; font-size: 20px; color: var(--brass); font-weight: 400; }
  .prompt-index .row .name { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; letter-spacing: -0.005em; }
  .prompt-index .row .count { font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); }
  .prompt-index .expanded ul {
    list-style: none; padding: 8px 0 22px 62px; margin: 0;
    border-bottom: 1px solid var(--hair);
  }
  .prompt-index .expanded li {
    font-family: 'Merriweather', serif; font-style: italic; font-size: 15px; line-height: 1.6; color: var(--ink);
    padding: 6px 0;
  }
  .prompt-index .expanded li::before { content: '· '; color: var(--brass); font-weight: 700; }

  /* Layered context — editorial index rows */
  .layered {
    padding-top: 60px; border-top: 3px double var(--hair-2);
  }
  .layered .lh {
    display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 30px;
  }
  .layered .lh h4 { font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 700; margin: 0; letter-spacing: -0.01em; }
  .layered .lh .r { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); }
  .layered .lh .r .b { color: var(--brass); cursor: pointer; }
  .layered .cols { display: grid; grid-template-columns: repeat(4, 1fr); gap: 48px; }
  .layered .col .col-h {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 12px; border-bottom: 2px solid var(--brass);
  }
  .layered .col .col-h .n {
    font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; letter-spacing: -0.005em;
  }
  .layered .col .col-h .c {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; color: var(--stone);
  }
  .layered .col .item {
    padding: 14px 0; border-bottom: 1px dotted var(--hair-2); cursor: pointer;
  }
  .layered .col .item:hover .t { color: var(--navy); }
  .layered .col .item .t {
    font-family: 'Merriweather', serif; font-size: 14px; line-height: 1.5; color: var(--ink);
    transition: color .15s;
  }
  .layered .col .item .d {
    font-family: 'IBM Plex Mono', monospace; font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone); margin-top: 6px;
  }
  .layered .col .more {
    font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase;
    color: var(--brass); padding: 14px 0 0; cursor: pointer;
  }

  /* Callouts */
  .callout {
    margin: 42px 0; padding: 32px 40px; background: var(--paper);
    border-left: 4px solid var(--brass); border-right: 1px solid var(--hair);
    position: relative;
  }
  .callout::before {
    content: '§'; position: absolute; top: 20px; right: 24px;
    font-family: 'Playfair Display', serif; font-size: 44px; color: var(--brass-soft); font-style: italic; line-height: 1;
  }
  .callout .kicker { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--brass); }
  .callout h3 { font-family: 'Playfair Display', serif; margin: 10px 0 12px; font-size: 26px; font-weight: 700; letter-spacing: -0.01em; }
  .callout p { font-family: 'Merriweather', serif; font-size: 15px; line-height: 1.75; margin: 10px 0; max-width: 900px; }
  .callout.navy { border-left-color: var(--navy); }
  .callout.navy .kicker { color: var(--navy); }
  .callout.navy::before { color: var(--navy-soft); }

  /* Imagery principles gallery */
  .img-gallery { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 22px; }
  @media (max-width: 980px) { .img-gallery { grid-template-columns: repeat(2, 1fr); } }
  .img-tile { background: var(--card); }
  .img-tile .tile-image {
    height: 180px; position: relative;
  }
  .img-tile.a .tile-image { background: var(--tex-boardroom); background-size: cover; }
  .img-tile.b .tile-image {
    background: var(--paper);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Playfair Display', serif; font-size: 90px; font-weight: 900; color: var(--navy); line-height: 1;
    letter-spacing: -0.03em;
  }
  .img-tile.c .tile-image { background: var(--tex-paper); background-size: cover; }
  .img-tile.d .tile-image {
    background: var(--tex-marble); background-size: cover;
    display: flex; align-items: center; justify-content: center;
  }
  .img-tile.d .tile-image::before {
    content: ''; width: 40%; height: 40%; border: 1.5px solid var(--navy); border-radius: 50%; opacity: .4;
  }
  .img-tile .info { padding: 14px 4px 0; }
  .img-tile .k { font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--brass); }
  .img-tile .n { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; margin-top: 3px; }
  .img-tile .d { font-family: 'Merriweather', serif; font-style: italic; font-size: 12.5px; line-height: 1.55; color: var(--stone); margin-top: 6px; }

  /* Mobile phones */
  .phones-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 30px; }
  @media (max-width: 1080px) { .phones-row { grid-template-columns: 1fr; } }
  .phone { width: 375px; margin: 0 auto; border: 1px solid var(--hair-2); border-radius: 26px; padding: 6px; background: var(--paper); box-shadow: 0 14px 36px rgba(20,20,20,0.08); }
  .phone-inner { background: var(--card); border-radius: 20px; overflow: hidden; }
  .m-notch { height: 14px; display: flex; align-items: center; justify-content: center; }
  .m-notch::before { content: ''; width: 52px; height: 4px; background: var(--stone); border-radius: 4px; opacity: .3; }
  .m-mast {
    padding: 12px 16px; display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 1px solid var(--ink);
  }
  .m-mast .brand { font-family: 'Playfair Display', serif; font-weight: 700; font-size: 14px; }
  .m-mast .brand em { font-style: italic; color: var(--brass); font-weight: 400; }
  .m-mast .date { font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); }
  .m-cover-img {
    height: 130px; background: var(--tex-boardroom); background-size: cover; position: relative;
  }
  .m-cover-img::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(19,18,17,0.6) 100%);
  }
  .m-cover-img .cap {
    position: absolute; left: 12px; right: 12px; bottom: 8px;
    font-family: 'Playfair Display', serif; font-style: italic; font-size: 12.5px; color: var(--card);
    line-height: 1.35; text-shadow: 0 2px 20px rgba(0,0,0,.5);
  }
  .m-arc { padding: 12px 16px 6px; }
  .m-fw { font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--stone); text-align: center; padding: 4px 0; }
  .m-nodes { display: grid; grid-template-columns: repeat(4, 1fr); text-align: center; padding: 6px 0; }
  .m-node {
    padding-top: 20px; position: relative;
    font-family: 'Playfair Display', serif; font-weight: 700; font-size: 14px;
  }
  .m-node .rn { font-family: 'Playfair Display', serif; font-style: italic; font-size: 9.5px; color: var(--brass); display: block; margin-top: 2px; letter-spacing: 0.04em; font-weight: 400; }
  .m-node::before {
    content: ''; position: absolute; top: 3px; left: 50%; margin-left: -4px;
    width: 8px; height: 8px; border-radius: 50%; background: var(--card); border: 1.4px solid var(--stone-2);
  }
  .m-node.current::before { background: var(--brass); border-color: var(--brass); }
  .m-node.selected::before { background: var(--navy); border-color: var(--navy); box-shadow: 0 0 0 4px rgba(20,38,79,.14); }
  .m-node.recede { opacity: .32; }
  .m-ribbon { padding: 8px 16px; font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone); border-top: 1px solid var(--navy); border-bottom: 1px solid var(--navy); background: var(--paper); }
  .m-ribbon .cur { color: var(--navy); }
  .m-canvas { padding: 16px 18px; }
  .m-headline-kicker { font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--brass); }
  .m-headline { font-family: 'Playfair Display', serif; font-weight: 700; font-size: 22px; line-height: 1.1; letter-spacing: -0.01em; margin-top: 4px; }
  .m-deck { font-family: 'Merriweather', serif; font-style: italic; font-size: 13px; line-height: 1.5; margin-top: 10px; color: var(--stone); border-left: 2px solid var(--brass-soft); padding-left: 10px; }
  .m-tab-row { display: flex; gap: 2px; margin: 14px 0 12px; border-bottom: 1px solid var(--hair); overflow-x: auto; }
  .m-tab { font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.2em; text-transform: uppercase; padding: 8px 10px; color: var(--stone); border-bottom: 2px solid transparent; white-space: nowrap; }
  .m-tab.active { color: var(--navy); border-bottom-color: var(--navy); font-weight: 500; }
  .m-tab.brass.active { color: var(--brass); border-bottom-color: var(--brass); }
  .m-cr-lead { font-family: 'Merriweather', serif; font-size: 14px; line-height: 1.65; }
  .m-cr-lead::first-letter { font-family: 'Playfair Display', serif; font-size: 40px; font-weight: 900; line-height: 0.9; float: left; margin: 3px 6px 0 0; color: var(--navy); }
  .m-cr-meta { font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--stone); margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--hair); }
  .m-actions { margin-top: 12px; display: flex; flex-direction: column; gap: 6px; }
  .m-btn-primary { text-align: center; font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase; padding: 10px; background: var(--navy); color: var(--card); border-radius: 999px; }
  .m-btn-ghost { text-align: center; font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase; padding: 8px; color: var(--brass); border-bottom: 1px solid var(--brass); }
  .m-jot { border-left: 1px solid var(--hair-2); padding: 10px 0 10px 14px; margin-top: 4px; position: relative; }
  .m-jot::before { content: ''; position: absolute; left: -1px; top: 10px; width: 3px; height: 24px; background: var(--brass); }
  .m-jot-body { font-family: 'Cormorant Garamond', 'Merriweather', serif; font-style: italic; font-weight: 500; font-size: 15px; line-height: 1.5; color: var(--ink); }
  .m-vis { display: flex; align-items: center; gap: 6px; margin-top: 12px; font-family: 'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--stone); }
  .m-vis .dot { width: 5px; height: 5px; border-radius: 50%; background: var(--stone); }
  .phone-lbl { text-align: center; margin-top: 14px; font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); }

  /* Design system table */
  .ds-table { width: 100%; border-collapse: collapse; margin-top: 22px; }
  .ds-table th, .ds-table td { text-align: left; padding: 14px 16px; font-size: 13.5px; line-height: 1.6; vertical-align: top; }
  .ds-table th { font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone); font-weight: 500; background: var(--paper); border-bottom: 1px solid var(--hair); }
  .ds-table td { border-bottom: 1px solid var(--hair); }
  .ds-table td strong { font-weight: 600; font-family: 'Merriweather', serif; }
  .badge { display: inline-block; font-family: 'IBM Plex Mono', monospace; font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; padding: 2px 8px; border: 1px solid var(--hair-2); color: var(--stone); }
  .badge.reuse { border-color: var(--stone); color: var(--stone); }
  .badge.adapt { border-color: var(--brass); color: var(--brass); }
  .badge.new   { border-color: var(--navy); color: var(--navy); }

  /* Recommendation */
  .recommendation {
    margin-top: 108px; padding: 56px 60px 60px;
    background: linear-gradient(180deg, var(--paper) 0%, var(--card) 60%);
    border: 1px solid var(--navy);
    position: relative;
  }
  .recommendation::before {
    content: '§'; position: absolute; top: 24px; right: 36px;
    font-family: 'Playfair Display', serif; font-size: 68px; font-style: italic; color: var(--navy-soft); line-height: 1;
  }
  .recommendation .k { font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--brass); font-weight: 500; }
  .recommendation h2 { font-family: 'Playfair Display', serif; font-size: 44px; font-weight: 700; margin: 14px 0 24px; line-height: 1.05; letter-spacing: -0.02em; max-width: 1020px; }
  .recommendation p { font-family: 'Merriweather', serif; font-size: 16px; line-height: 1.75; max-width: 880px; }
  .principle-list { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px 44px; margin-top: 30px; }
  @media (max-width: 900px) { .principle-list { grid-template-columns: 1fr; } }
  .principle-list li { list-style: none; padding-left: 20px; position: relative; font-family: 'Merriweather', serif; font-size: 14px; line-height: 1.7; }
  .principle-list li::before { content: '§'; color: var(--brass); position: absolute; left: 0; font-style: italic; font-family: 'Playfair Display', serif; font-size: 16px; top: -2px; }
  .principle-list li strong { font-family: 'Playfair Display', serif; font-weight: 700; }

  .footer-note { margin-top: 68px; padding-top: 26px; border-top: 1px solid var(--hair); color: var(--stone); font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.16em; }
</style>
</head>
<body>
<div class="wrap">

  <!-- ═════════ COVER ═════════ -->
  <section class="cover">
    <div class="kicker">Yadid Advisory · YadidOS · Volume IV · The Embrace Instrument · Feb 2026</div>
    <h1 class="display">The Embrace tab, reset as an editorial instrument for strategic reflection.</h1>
    <p class="lede">This round refines the approved single-map direction with the qualities of a premium editorial spread — refined typography, strong hierarchy, generous spacing, calm structure, elegant readability — interpreted through the YadidOS palette. Imagery is integrated with restraint: a masthead atmosphere, a client monogram plate, a supporting reading-column plate, small avatars on bylines. Every image earns its place; none decorate. The Method map remains the single visual anchor.</p>
    <div class="byline">
      <span>Design direction</span><span class="sep">/</span>
      <span>Round IV</span><span class="sep">/</span>
      <span>Preview only · no production code changed</span><span class="sep">/</span>
      <span>Palette · Ivory · Navy Ink · Stone · Black · restrained Brass</span>
    </div>
    <div class="toc">
      <a href="#s1">01 · The editorial system</a>
      <a href="#s2">02 · The tab as a front page</a>
      <a href="#s3">03 · Selected · the canvas</a>
      <a href="#s4">04 · Imagery, restrained</a>
      <a href="#s5">05 · Prompts as an index</a>
      <a href="#s6">06 · Layered context</a>
      <a href="#s7">07 · Mobile</a>
      <a href="#s8">08 · Components</a>
      <a href="#reco">→ Recommendation</a>
    </div>
  </section>

  <!-- ═════════ 01 THE EDITORIAL SYSTEM ═════════ -->
  <section class="section" id="s1">
    <div class="section-head">
      <div class="num">01</div>
      <h2 class="display">The editorial system — five moves that carry the whole surface</h2>
      <div class="sub">Not a theme, not a skin — a small set of typographic and structural moves used consistently across the tab. Together they read as boutique advisory rather than software.</div>
    </div>

    <div class="callout navy">
      <div class="kicker">The five moves</div>
      <p><strong>1 · Display serif for the voice.</strong> Playfair Display, 900-weight, tight tracking. Reserved for the single moment on each screen that deserves display treatment — the framing headline, the client's monogram, the section titles. Never on buttons, never on metadata.</p>
      <p><strong>2 · Body serif for reading.</strong> Merriweather at 15.5–18px, generous 1.7 leading, rag-right paragraphs, dropcap on the first paragraph of the Current Reflection. This is the "reading column" tradition — a book, not a form.</p>
      <p><strong>3 · A hand for margins.</strong> Cormorant Garamond italic for the Working Journal writing. Reads as annotation, not authored copy. Softer weight than Merriweather. The eye immediately knows it's provisional.</p>
      <p><strong>4 · Mono for the "chrome."</strong> IBM Plex Mono, small caps letter-spacing 0.22–0.32em. Every rubric, every metadata line, every button. Consistent enough that a reader unconsciously learns "if it's mono, it's a signal, not content."</p>
      <p><strong>5 · Structural hairlines, not cards.</strong> Rhythm comes from hairlines (single, double, triple-double for major separations), rules with roman numerals and section numbers, and section eyebrows — the vocabulary of a printed masthead. Only ONE outer container border on the whole tab.</p>
    </div>
  </section>

  <!-- ═════════ 02 FRONT PAGE ═════════ -->
  <section class="section" id="s2">
    <div class="section-head">
      <div class="num">02</div>
      <h2 class="display">The Embrace tab as a front page — masthead, atmosphere, index</h2>
      <div class="sub">Idle state. Before any movement is selected. The advisor lands on a composed page, not a dashboard shell. A client monogram plate anchors the left; a single atmospheric image and dateline anchor the right; below, the Method map is drawn as the day's story index.</div>
    </div>

    <div class="stage">
      <div class="stage-caption">
        <span>Desktop · 1440 × ⋯ · Embrace tab · idle</span>
        <span class="r">Front page</span>
      </div>

      <!-- Masthead -->
      <div class="masthead">
        <div>
          <div class="brand">Yadid <em>Embrace</em></div>
          <div class="brand-sub">The strategic reflection instrument</div>
        </div>
        <div class="dateline">
          Osma Group · Q3 Governance<br>
          <span class="cur">Current emphasis · Reimagine</span> · Last reflected 3 days ago
        </div>
      </div>
      <div class="rule-double"></div>

      <!-- Cover band: client monogram + atmosphere image -->
      <div class="cover-band">
        <div class="client-card">
          <div>
            <div class="k">The engagement</div>
            <div class="monogram">OG</div>
            <div class="name">Osma Group</div>
            <div class="meta">Founder-CEO handoff<br>Q3 · 2026 · Governance track<br>Lead partner · J. Rojo</div>
          </div>
          <div class="k" style="border-top: 1px solid var(--hair-2); padding-top: 12px; color: var(--brass);">§ Volume III</div>
        </div>
        <div class="atmosphere">
          <div class="cap">
            <div class="title">"Naming the pattern under the noise" — the current strategic question this engagement is holding.</div>
            <div class="credit">Atmosphere · Boardroom study</div>
          </div>
        </div>
      </div>

      <!-- Method map as story index -->
      <div class="method-plate">
        <div class="rule-head">
          <div class="k">The Yadid Embrace · Story index</div>
          <div class="hint">Select a movement or a continuous dynamic below to open the reflection article</div>
        </div>
        <div class="fw-top"><span>Connections &amp; Relationships</span></div>
        <div class="movements">
          <div class="m"><div class="roman">I</div><div class="name">Listen</div><div class="fw">Discover</div><div class="intent">Diverge — sit with it before we name it</div></div>
          <div class="m"><div class="roman">II</div><div class="name">Reveal</div><div class="fw">Define</div><div class="intent">Converge — name the pattern under the noise</div></div>
          <div class="m current"><div class="roman">III</div><div class="name">Reimagine</div><div class="fw">Develop</div><div class="intent">Diverge — design many possible futures</div></div>
          <div class="m"><div class="roman">IV</div><div class="name">Guide</div><div class="fw">Deliver</div><div class="intent">Converge — walk with it, in the room</div></div>
        </div>
        <svg class="arcs" viewBox="0 0 800 56"><path d="M 100 28 C 220 -4 320 -4 400 28" fill="none" stroke="#C9C1B0" stroke-width="1.2"/><path d="M 500 28 C 600 -4 680 -4 780 28" fill="none" stroke="#C9C1B0" stroke-width="1.2"/></svg>
        <div class="fw-bot"><span>Leadership &amp; Storytelling</span></div>
      </div>
      <div class="idle-bar">
        <div class="l">§ Continuous dynamics — the conditions surrounding all four movements · <span class="b">Above · Connections &amp; Relationships</span> · <span class="b">Below · Leadership &amp; Storytelling</span></div>
        <div class="r">3 journal entries this week · 1 promoted to current reflection · Draft governance memo due Fri</div>
      </div>
    </div>

    <div class="callout">
      <div class="kicker">Why the masthead + monogram pairing</div>
      <p>An editorial masthead reads instantly as "this is a considered document." The Yadid <em>Embrace</em> wordmark and dateline give the tab identity without over-branding. On the left, a client-specific monogram card ("OG · Osma Group") replaces the anonymous "engagement selected" header we used before — it grounds the entire session in this particular engagement, this particular client, giving the surface warmth and continuity across sessions. The right atmosphere panel carries a client-provided or firm-selected image (a boardroom, a place, an object) that is muted, subordinate, and never illustrative of the specific reflection — its job is to <em>set the room's temperature</em>, not to depict content.</p>
    </div>
  </section>

  <!-- ═════════ 03 SELECTED · CANVAS ═════════ -->
  <section class="section" id="s3">
    <div class="section-head">
      <div class="num">03</div>
      <h2 class="display">Selected · the reflection article</h2>
      <div class="sub">A movement is selected. The Method compacts into an "index bar" at the top of the same container. Below the masthead, the reflection article opens as a full editorial spread — headline, deck, byline, lead paragraph with dropcap, supporting reading-column plate, and — facing the article — the Working Journal as a fluid editorial notebook.</div>
    </div>

    <div class="stage">
      <div class="stage-caption">
        <span>Desktop · Reveal selected · full canvas</span>
        <span class="r">The article</span>
      </div>

      <div class="masthead">
        <div>
          <div class="brand">Yadid <em>Embrace</em></div>
          <div class="brand-sub">The strategic reflection instrument</div>
        </div>
        <div class="dateline">
          Osma Group · Q3 Governance<br>
          <span class="cur">Article · Reveal — Define</span>
        </div>
      </div>
      <div class="rule-double"></div>

      <!-- Compact Method plate — still visible, now subordinate -->
      <div class="method-plate compact">
        <div class="fw-top fw-recede"><span>Connections &amp; Relationships</span></div>
        <div class="movements">
          <div class="m recede"><div class="roman">I</div><div class="name">Listen</div><div class="fw">Discover</div></div>
          <div class="m selected"><div class="roman">II</div><div class="name">Reveal</div><div class="fw">Define</div></div>
          <div class="m current recede"><div class="roman">III</div><div class="name">Reimagine</div><div class="fw">Develop</div></div>
          <div class="m recede"><div class="roman">IV</div><div class="name">Guide</div><div class="fw">Deliver</div></div>
        </div>
        <svg class="arcs" viewBox="0 0 800 32"><path d="M 100 16 C 220 -2 320 -2 400 16" fill="none" stroke="#C9C1B0" stroke-width="1" opacity=".55"/><path d="M 500 16 C 600 -2 680 -2 780 16" fill="none" stroke="#C9C1B0" stroke-width="1" opacity=".55"/></svg>
        <div class="fw-bot fw-recede"><span>Leadership &amp; Storytelling</span></div>
      </div>

      <!-- The article -->
      <div class="canvas">
        <!-- Ribbon appears only when opened from a non-engagement surface -->
        <div class="ribbon">
          <div class="trail">
            <span>Client · <strong style="color:var(--ink); font-weight:500;">Osma Group</strong></span>
            <span class="sep">›</span>
            <span>Engagement · <strong style="color:var(--ink); font-weight:500;">Q3 Governance</strong></span>
            <span class="sep">›</span>
            <span>Method · <span class="cur">Reveal · Connections &amp; Relationships</span></span>
          </div>
          <button class="return">Return to engagement ↗</button>
        </div>

        <!-- Headline block: title + deck on the left, supporting image on the right -->
        <div class="headline-block">
          <div class="h-main">
            <div class="rubric-line">
              <span class="r">§ II · Reveal</span>
              <span class="r-sub">Define · Converge — name it</span>
            </div>
            <h3 class="headline">Naming the pattern under the noise.</h3>
            <div class="deck">
              Where is the actual pattern under the noise — and what two or three sentences would let a partner walk into this cold and understand what's true?
            </div>
            <div class="byline-strip">
              <span class="avatar">JR</span>
              <span>By <span class="b">J. Rojo</span></span>
              <span class="sep">·</span>
              <span>Version <span class="b">IV</span> of VI</span>
              <span class="sep">·</span>
              <span>Updated <span class="b">3 days ago</span></span>
              <span class="sep">·</span>
              <span>Visible to <span class="b">advisory team</span></span>
            </div>
          </div>
          <div class="h-side">
            <div class="imagery"></div>
            <div class="caption"><span class="b">Plate I</span> · The boardroom that hosts the July conversation. Uploaded by J. Rojo · June 3.</div>
          </div>
        </div>

        <!-- The article body: Current Reflection (left, wider) and Working Journal (right) -->
        <div class="article-grid">
          <!-- CURRENT REFLECTION — long-form editorial article -->
          <div class="col cr">
            <div class="col-head">
              <div class="k">§ Current Reflection · governed</div>
              <div class="m">Present considered judgment</div>
            </div>
            <div class="cr-body">
              <p class="lead">The board is starting to see the founder-CEO handoff as a governance question, not a personality question. That reframe changes who needs to be in the room next month — chair and audit lead first, then the founder.</p>
              <p class="para">The right sequence protects both the mandate and the relationship. If the memo lands before the chair-founder conversation, we will have made a governance argument in the wrong room, and the founder will read it as a challenge to his authority rather than an offer of scaffolding.</p>
              <div class="plate"></div>
              <div class="plate-cap"><span style="color:var(--ink);">Plate II</span> · Draft memo cover, redacted — supporting document for the current reflection.</div>
              <p class="para">The two open questions we haven't yet answered: whose voice does the chair need in the room to feel supported when she raises the mandate concern; and what does the founder need to hear from us before that meeting to stay curious rather than defensive.</p>
              <div class="cr-actions">
                <button class="btn-pill">Update current reflection</button>
                <span class="link">§ See history · 3 prior versions</span>
              </div>
            </div>
          </div>

          <!-- WORKING JOURNAL — margin annotations -->
          <div class="col wj">
            <div class="col-head">
              <div class="k">§ Working Journal · provisional</div>
              <div class="m">Doubts · questions · first impressions</div>
            </div>
            <!-- Active writing area -->
            <div class="wj-jot">
              <div class="jot-body">Hunch: this is a governance framing failure more than a comms one. Worth testing with chair tomorrow before committing to the memo. What am I missing about the CFO's role in translating the board's mood?</div>
            </div>
            <div class="wj-controls">
              <div class="vis-chip team"><span class="dot"></span> Team ▾</div>
              <button class="btn-ghost-brass">Save to journal</button>
              <button class="link-stone">Promote to current reflection ↑</button>
              <button class="link-stone">Capture in Advisory Desk →</button>
            </div>
            <!-- Prior entries -->
            <div class="wj-entries">
              <div class="wj-entry">
                <div class="meta">
                  <span class="avatar">JR</span>
                  <span>Yesterday</span>
                  <span class="vis-chip private" style="font-size:8.5px;"><span class="dot"></span> Private</span>
                  <span style="color:var(--brass);">Reveal</span>
                </div>
                <div class="body-txt">First read of the chair breakfast — I don't yet trust the "governance" framing; it might be my instinct wanting a tidy narrative. Test it.</div>
                <div class="row-mini">
                  <button>Promote ↑</button>
                  <button class="text">Link a briefing</button>
                </div>
              </div>
              <div class="wj-entry">
                <div class="meta">
                  <span class="avatar">JR</span>
                  <span>2 days ago</span>
                  <span class="vis-chip team" style="font-size:8.5px;"><span class="dot"></span> Team</span>
                  <span style="color:var(--brass);">Seeded from — Chair breakfast</span>
                </div>
                <div class="body-txt">Chair specifically said she doesn't feel consulted before decisions. Note what she didn't say: nothing about the founder personally.</div>
              </div>
              <div class="wj-entry promoted">
                <div class="meta">
                  <span class="avatar">JR</span>
                  <span>4 days ago</span>
                  <span style="color:var(--stone);">Promoted → version III of Current Reflection</span>
                </div>
                <div class="body-txt">First hunch that the story might be governance-shaped. Kept as historical record.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Prompts as editorial index -->
        <div class="prompts-block">
          <div class="lbl-col">
            <div class="k">§ Prompts</div>
            The four Yadid groupings, progressively.
            <div class="depth">Each opens only when asked. The guided walk works through all four in order — an opt-in "reading path" through the movement.</div>
            <div style="margin-top: 24px;">
              <span class="link-stone">Walk me through all four →</span>
            </div>
          </div>
          <div class="prompt-index">
            <div class="row open">
              <div class="roman">I</div>
              <div class="name">Current reality</div>
              <div class="count">4 prompts · open</div>
            </div>
            <div class="expanded">
              <ul>
                <li>Which relationships currently carry the engagement — and whose is at risk?</li>
                <li>Where is trust real, and where is it borrowed?</li>
                <li>What is the story this work is telling internally — is it the one we want?</li>
                <li>Which access pathway are we relying on that we haven't yet secured?</li>
              </ul>
            </div>
            <div class="row">
              <div class="roman">II</div>
              <div class="name">System dynamics</div>
              <div class="count">3 prompts · ▸</div>
            </div>
            <div class="row">
              <div class="roman">III</div>
              <div class="name">Strategic need</div>
              <div class="count">3 prompts · ▸</div>
            </div>
            <div class="row">
              <div class="roman">IV</div>
              <div class="name">Next move</div>
              <div class="count">1 prompt · ▸</div>
            </div>
          </div>
        </div>

        <!-- Layered context as editorial index -->
        <div class="layered">
          <div class="lh">
            <h4>What surrounds this reflection</h4>
            <div class="r">12 items · top 8 shown · <span class="b">Hide layer</span></div>
          </div>
          <div class="cols">
            <div class="col">
              <div class="col-h"><span class="n">Briefings</span><span class="c">3</span></div>
              <div class="item"><div class="t">Chair breakfast — mandate concerns</div><div class="d">3d · linked</div></div>
              <div class="item"><div class="t">Founder session · story reframe</div><div class="d">1w</div></div>
              <div class="more">+ 1 more →</div>
            </div>
            <div class="col">
              <div class="col-h"><span class="n">Tasks</span><span class="c">2</span></div>
              <div class="item"><div class="t">Draft governance memo · Fri</div><div class="d">Fatima · high</div></div>
              <div class="item"><div class="t">Prep chair 1:1</div><div class="d">Wed</div></div>
            </div>
            <div class="col">
              <div class="col-h"><span class="n">Decisions</span><span class="c">2</span></div>
              <div class="item"><div class="t">Board offsite dates confirmed</div><div class="d">Aug 12</div></div>
              <div class="item"><div class="t">Delay founder announcement</div><div class="d">Jun 20</div></div>
            </div>
            <div class="col">
              <div class="col-h"><span class="n">Prior reflections</span><span class="c">5</span></div>
              <div class="item"><div class="t">Prev current reflection · v3</div><div class="d">1w</div></div>
              <div class="item"><div class="t">Journal · governance framing hunch</div><div class="d">4d</div></div>
              <div class="more">+ 3 more →</div>
            </div>
          </div>
        </div>

      </div><!-- /.canvas -->
    </div>

    <div class="callout navy">
      <div class="kicker">What the editorial upgrade gives us that Round 3 didn't</div>
      <p><strong>Headline → deck → byline → lead → article body.</strong> That is the sequence of an editorial spread, and it is exactly the sequence a reflective advisor works through when they open a movement. Round 3 had these elements individually; Round 4 arranges them so the eye moves through them the way it would move through a Sunday review page.</p>
      <p><strong>Roman numerals on the four movements.</strong> They are not "phases" or "steps." They are movements in a piece — I, II, III, IV. This is a subtle typographic upgrade that reinforces method integrity while adding the elegance of a musical score or a book's table of contents.</p>
      <p><strong>Current Reflection and Working Journal now face each other across a rule.</strong> Not because they are equal — the Current Reflection is a wider article column with a real dropcap; the Journal is a slimmer margin column with italic script — but because that facing arrangement is how a considered document sets its provisional thinking against its considered judgment. The visual weight is asymmetric on purpose.</p>
      <p><strong>Restrained imagery.</strong> Three plates on the whole page: the atmosphere card in the masthead, the reading-column plate inside the Current Reflection, small avatars on bylines. That is <em>less</em> imagery than a typical dashboard's icons and illustrations, but it is dramatically more expressive because each image is intentional.</p>
    </div>
  </section>

  <!-- ═════════ 04 IMAGERY ═════════ -->
  <section class="section" id="s4">
    <div class="section-head">
      <div class="num">04</div>
      <h2 class="display">Imagery, restrained — four uses, no more</h2>
      <div class="sub">Every image earns its place. No stock illustrations. No emoji-style icons. No decorative fills. Four disciplined uses, each with its own aspect ratio, treatment, and caption pattern.</div>
    </div>

    <div class="img-gallery">
      <div class="img-tile a">
        <div class="tile-image"></div>
        <div class="info">
          <div class="k">§ Use I</div>
          <div class="n">Atmosphere</div>
          <div class="d">A single muted image in the masthead. Boardroom, place, object — never a person. Its job is to set the room's temperature, not depict content.</div>
        </div>
      </div>
      <div class="img-tile b">
        <div class="tile-image">OG</div>
        <div class="info">
          <div class="k">§ Use II</div>
          <div class="n">Monogram</div>
          <div class="d">Client's initials rendered in Playfair Display 900. Fallback for clients who haven't uploaded a mark. Becomes the client's signature across every session.</div>
        </div>
      </div>
      <div class="img-tile c">
        <div class="tile-image"></div>
        <div class="info">
          <div class="k">§ Use III</div>
          <div class="n">Reading plate</div>
          <div class="d">A supporting image or scan inside the Current Reflection article — a redacted memo cover, a document detail, a chart. Always captioned "Plate II · ..."</div>
        </div>
      </div>
      <div class="img-tile d">
        <div class="tile-image"></div>
        <div class="info">
          <div class="k">§ Use IV</div>
          <div class="n">Avatar</div>
          <div class="d">Small circular byline mark for every author. Real photo when uploaded; initials on a paper texture otherwise. 22px in journal entries, 26px in the byline strip.</div>
        </div>
      </div>
    </div>

    <div class="callout">
      <div class="kicker">Discipline rules</div>
      <p><strong>Aspect &amp; scale.</strong> Atmosphere = 16:9 wide, masthead-flush. Monogram = 1:1 square, 240×240 in the client card. Reading plate = 5:3 landscape, at reading-column width. Avatars = perfect circles, 22 or 26px.</p>
      <p><strong>Treatment.</strong> All atmosphere images receive a bottom-third gradient (transparent → 75% ink) so caption text is always legible without a plate. Reading plates get a hairline border and a Roman-numbered caption below. Monograms use Navy 900 on Paper.</p>
      <p><strong>Never.</strong> No stock photography. No inline product icons. No Lucide/Phosphor decorative glyphs on the map itself (they remain in the shell chrome only). No image behind text as a background wash.</p>
      <p><strong>When absent.</strong> If a client hasn't uploaded any imagery, the atmosphere panel falls back to a Yadid-generated marble/paper texture with the framing question over it in italic — the same treatment used for the reading-column plate placeholder. The surface never looks broken.</p>
    </div>
  </section>

  <!-- ═════════ 05 PROMPTS AS INDEX ═════════ -->
  <section class="section" id="s5">
    <div class="section-head">
      <div class="num">05</div>
      <h2 class="display">Prompts as an index, not a form</h2>
      <div class="sub">The four Yadid groupings are drawn as a reading path — Roman numerals, ranked titles, prompt counts. One open at a time by default. Selecting a row expands its prompts in an italic list with brass ornaments, not into a form.</div>
    </div>

    <p style="font-family:'Merriweather', serif; font-size: 14.5px; line-height: 1.7; color: var(--stone); max-width: 780px; margin-top: 10px;">
      This treatment is already shown in the article stage above (§03). The point of doing it as an index rather than as four bordered cards is to preserve the editorial rhythm: the eye reads down a numbered list rather than sideways across a card grid. It also gives us the "guided walk" affordance for free — a partner asking "walk me through all four →" is asking to read the index in sequence.
    </p>
  </section>

  <!-- ═════════ 06 LAYERED CONTEXT ═════════ -->
  <section class="section" id="s6">
    <div class="section-head">
      <div class="num">06</div>
      <h2 class="display">Layered context, as an editorial index</h2>
      <div class="sub">Four categories, brass-underlined column headers, plain-text item titles, mono metadata below. Hidden by default; toggle opens under the article. No cards, no icons, no colored chips.</div>
    </div>

    <p style="font-family:'Merriweather', serif; font-size: 14.5px; line-height: 1.7; color: var(--stone); max-width: 780px; margin-top: 10px;">
      This treatment is also shown in the article stage above (§03). Clicking any item opens the source record in place — Briefings open the Advisory Desk dialog, Tasks open the task drawer, Decisions open the decision detail sheet, prior Reflections re-open the same Embrace article at that entry. Nothing is duplicated; every item is a pointer to the canonical record. The Method diagram is never annotated.
    </p>
  </section>

  <!-- ═════════ 07 MOBILE ═════════ -->
  <section class="section" id="s7">
    <div class="section-head">
      <div class="num">07</div>
      <h2 class="display">Mobile — the same editorial voice, sequenced</h2>
      <div class="sub">Masthead compressed to a single line. The atmosphere image becomes a thin cover strip with the framing question over it. Article layers become tabs in the mental order of reflection. The dropcap is preserved on the lead paragraph; the italic Journal script is preserved.</div>
    </div>

    <div class="phones-row">
      <div>
        <div class="phone"><div class="phone-inner">
          <div class="m-notch"></div>
          <div class="m-mast">
            <div class="brand">Yadid <em>Embrace</em></div>
            <div class="date">Osma Group · Q3</div>
          </div>
          <div class="m-cover-img">
            <div class="cap">"Naming the pattern under the noise" — the question this engagement is holding.</div>
          </div>
          <div class="m-arc">
            <div class="m-fw">Connections &amp; Relationships</div>
            <div class="m-nodes">
              <div class="m-node">Listen<span class="rn">I</span></div>
              <div class="m-node">Reveal<span class="rn">II</span></div>
              <div class="m-node current">Reimagine<span class="rn">III</span></div>
              <div class="m-node">Guide<span class="rn">IV</span></div>
            </div>
            <div class="m-fw">Leadership &amp; Storytelling</div>
          </div>
          <div style="padding: 22px 20px 26px; text-align: center;">
            <div style="font-family:'IBM Plex Mono', monospace; font-size: 8.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--stone);">Tap a movement or dynamic · <span style="color:var(--brass);">current emphasis · Reimagine</span></div>
          </div>
        </div></div>
        <div class="phone-lbl">01 · Idle · front page</div>
      </div>
      <div>
        <div class="phone"><div class="phone-inner">
          <div class="m-notch"></div>
          <div class="m-mast">
            <div class="brand">Yadid <em>Embrace</em></div>
            <div class="date">Article II · Reveal</div>
          </div>
          <div class="m-arc">
            <div class="m-fw" style="opacity:.35;">Connections &amp; Relationships</div>
            <div class="m-nodes">
              <div class="m-node recede">Listen<span class="rn">I</span></div>
              <div class="m-node selected">Reveal<span class="rn">II</span></div>
              <div class="m-node current recede">Reimagine<span class="rn">III</span></div>
              <div class="m-node recede">Guide<span class="rn">IV</span></div>
            </div>
          </div>
          <div class="m-ribbon"><span>Osma Group</span> › <span>Q3 Governance</span> › <span class="cur">Reveal</span></div>
          <div class="m-canvas">
            <div class="m-headline-kicker">§ II · Reveal · Define</div>
            <div class="m-headline">Naming the pattern under the noise.</div>
            <div class="m-deck">Where is the actual pattern — and what two or three sentences would let a partner walk in cold?</div>
            <div class="m-tab-row">
              <div class="m-tab">Framing</div>
              <div class="m-tab active">Current</div>
              <div class="m-tab">Prompts</div>
              <div class="m-tab brass">Journal</div>
              <div class="m-tab">Linked</div>
            </div>
            <div class="m-cr-lead">The board is starting to see the founder-CEO handoff as a governance question, not a personality question.</div>
            <div class="m-cr-meta">Version IV · J. Rojo · updated 3d</div>
            <div class="m-actions">
              <div class="m-btn-primary">Update current reflection</div>
              <div class="m-btn-ghost" style="color:var(--stone); border-bottom:1px solid var(--stone);">§ See history · 3 versions</div>
            </div>
          </div>
        </div></div>
        <div class="phone-lbl">02 · Article · Current tab</div>
      </div>
      <div>
        <div class="phone"><div class="phone-inner">
          <div class="m-notch"></div>
          <div class="m-mast">
            <div class="brand">Yadid <em>Embrace</em></div>
            <div class="date">Article II · Reveal</div>
          </div>
          <div class="m-arc">
            <div class="m-fw" style="opacity:.35;">Connections &amp; Relationships</div>
            <div class="m-nodes">
              <div class="m-node recede">Listen<span class="rn">I</span></div>
              <div class="m-node selected">Reveal<span class="rn">II</span></div>
              <div class="m-node current recede">Reimagine<span class="rn">III</span></div>
              <div class="m-node recede">Guide<span class="rn">IV</span></div>
            </div>
          </div>
          <div class="m-ribbon"><span>Osma Group</span> › <span class="cur">Reveal</span></div>
          <div class="m-canvas">
            <div class="m-tab-row">
              <div class="m-tab">Framing</div>
              <div class="m-tab">Current</div>
              <div class="m-tab">Prompts</div>
              <div class="m-tab brass active">Journal</div>
              <div class="m-tab">Linked</div>
            </div>
            <div class="m-jot"><div class="m-jot-body">Hunch: governance framing failure, not comms. Test with chair. What am I missing about the CFO?</div></div>
            <div class="m-vis"><span class="dot"></span> Team ▾ · <span style="color:var(--brass);">Save to journal</span></div>
            <div style="margin-top: 20px; padding-top: 12px; border-top: 1px dashed var(--hair-2);">
              <div class="eyebrow" style="font-size:8.5px; color:var(--brass);">§ Prior · 3 entries</div>
              <div class="m-jot" style="margin-top: 10px;"><div class="m-jot-body" style="font-size: 13.5px;">Chair doesn't feel consulted before decisions.</div></div>
            </div>
          </div>
        </div></div>
        <div class="phone-lbl">03 · Article · Journal tab</div>
      </div>
    </div>
  </section>

  <!-- ═════════ 08 COMPONENTS ═════════ -->
  <section class="section" id="s8">
    <div class="section-head">
      <div class="num">08</div>
      <h2 class="display">Components — what to reuse, adapt, and build</h2>
      <div class="sub">The editorial upgrade requires the same seven new components proposed in Round 3, plus three imagery components. Nothing is invented that shadcn already provides.</div>
    </div>

    <table class="ds-table">
      <tr><th style="width:200px;">Component</th><th>Status</th><th>Purpose</th></tr>
      <tr><td><strong>EmbraceMasthead</strong></td><td><span class="badge new">New</span></td><td>Renders the Yadid <em>Embrace</em> wordmark, brand-sub, and dateline. Full-width, ink-underlined. Reads the engagement's name, client, and emphasis from route state.</td></tr>
      <tr><td><strong>ClientMonogramCard</strong></td><td><span class="badge new">New</span></td><td>Left cover-band panel. Displays initials in Playfair 900 by default; swaps to an uploaded image when present. Includes engagement metadata (year, lead partner, track name).</td></tr>
      <tr><td><strong>AtmospherePanel</strong></td><td><span class="badge new">New</span></td><td>Right cover-band image with framing-question caption. Supports client-uploaded or firm-selected imagery; falls back to a Yadid marble/paper texture.</td></tr>
      <tr><td><strong>MapCanvas</strong></td><td><span class="badge new">New</span></td><td>The single-container map + expandable canvas. Owns expand/compact motion, keyboard focus, prefers-reduced-motion. Roman numerals on nodes, method-plate layout.</td></tr>
      <tr><td><strong>HeadlineBlock</strong></td><td><span class="badge new">New</span></td><td>Kicker rubric · Playfair 900 headline · italic deck with brass hairline · byline strip with avatar. Renders the article's masthead.</td></tr>
      <tr><td><strong>CurrentReflectionArticle</strong></td><td><span class="badge new">New</span></td><td>Long-form editorial column. Merriweather 18px lead with Navy dropcap · Merriweather 15.5px body · optional reading plate with Roman-numbered caption · pill "Update current reflection" + "See history" link.</td></tr>
      <tr><td><strong>WorkingJournalColumn</strong></td><td><span class="badge new">New</span></td><td>Editorial notebook. Cormorant italic writing area · brass tick + hairline · avatar + visibility chip per entry · brass-underlined save · promote link.</td></tr>
      <tr><td><strong>VisibilityChip</strong></td><td><span class="badge new">New</span></td><td>Inline three-level chip (Private · Team · Firm). DropdownMenu under the hood. Never becomes a modal.</td></tr>
      <tr><td><strong>PromptIndex</strong></td><td><span class="badge new">New</span></td><td>Editorial index rows with Roman numerals · progressive-disclosure prompts as italic list with brass ornaments.</td></tr>
      <tr><td><strong>LayeredContextIndex</strong></td><td><span class="badge new">New</span></td><td>Four-column brass-underlined column headers · plain-text item titles · mono metadata · "+ N more" opens shadcn Sheet drawer.</td></tr>
      <tr><td><strong>ContextRibbon</strong></td><td><span class="badge new">New</span></td><td>Navy-ruled breadcrumb + "Return to engagement" pill. Appears only when opened from a non-engagement surface.</td></tr>
      <tr><td><strong>MethodArcs</strong></td><td><span class="badge adapt">Adapt</span></td><td>Add compact prop, Roman-numeral overlay, framework-label rules on either side of the arcs.</td></tr>
      <tr><td><strong>Sheet</strong> (shadcn)</td><td><span class="badge reuse">Reuse</span></td><td>"+ N more" drawers · mobile Update-Current confirmation bottom sheet.</td></tr>
      <tr><td><strong>DropdownMenu</strong> (shadcn)</td><td><span class="badge reuse">Reuse</span></td><td>Visibility chip · movement quick-switcher.</td></tr>
      <tr><td><strong>Textarea</strong> (shadcn)</td><td><span class="badge reuse">Reuse</span></td><td>Underneath the WorkingJournal and Update-Current composers.</td></tr>
      <tr><td><strong>Tabs</strong> (shadcn)</td><td><span class="badge reuse">Reuse</span></td><td>Mobile article tabs (Framing / Current / Prompts / Journal / Linked).</td></tr>
      <tr><td><strong>Dialog</strong> (shadcn)</td><td><span class="badge reuse">Reuse</span></td><td>"See history" — renders the reflection_history stack.</td></tr>
      <tr><td><strong>Font stack additions</strong></td><td><span class="badge new">New</span></td><td>Add Playfair Display (weights 700, 900) and Cormorant Garamond (weight 500, 500-italic) to <code>/frontend/public/index.html</code>. Font-display: swap. ~48KB total added.</td></tr>
    </table>
  </section>

  <!-- ═════════ RECOMMENDATION ═════════ -->
  <section class="recommendation" id="reco">
    <div class="k">Recommendation · Round IV</div>
    <h2 class="display">Ship this editorial direction. It preserves every functional decision from Rounds 2 and 3 and adds the visual sophistication of a boutique advisory instrument.</h2>

    <p><strong>The Embrace tab reads as a considered document.</strong> Masthead, dateline, monogram, atmosphere image, story index, article, journal, index of prompts, index of context. Every element belongs to that vocabulary. Every element earns its place.</p>

    <p><strong>The Method map remains the single visual anchor.</strong> No second diagram. No competing visualization. The map is the front page's story index; when a movement is selected, the map compacts into an article-index bar at the top of the same container, the arcs and framework labels dim to <code>.35–.40</code>, and the article opens below in the same page.</p>

    <p><strong>Imagery is disciplined into four uses.</strong> Atmosphere panel · client monogram · reading plate · byline avatar. No stock. No decoration. When a client has not uploaded imagery, elegant Yadid-generated textures fill the atmosphere and plate slots. The surface never looks empty and never looks noisy.</p>

    <p><strong>Every functional decision from Round 3 is preserved.</strong> Two distinct save actions with different visual weight. Three-level visibility chip inline on every entry. Cross-surface context ribbon with reliable return path. Layered context hidden by default. Guided reflection as an opt-in. Same data model — <code>where_we_are_now</code> as Current Reflection, <code>reflections[]</code> as Working Journal, four new fields (<code>visibility</code>, <code>status</code>, <code>promoted_at</code>, <code>source_note_id</code>). Nothing is re-litigated.</p>

    <p><strong>Cost of the upgrade.</strong> Two additional Google Font families (Playfair Display and Cormorant Garamond, ~48KB gzipped total) served with <code>font-display: swap</code>. Roughly seven new named React components (all listed above). No new backend endpoints beyond the four already proposed in Round 3.</p>

    <ul class="principle-list">
      <li><strong>Editorial, not administrative.</strong> The advisor feels they are writing in a leather-bound file, not filling in a form.</li>
      <li><strong>Method integrity.</strong> I · II · III · IV as Roman numerals. Continuous conditions remain conditions, above and below the arcs — never promoted to movements.</li>
      <li><strong>One map, one surface, one story.</strong> The map compacts into itself. Never a second diagram.</li>
      <li><strong>Imagery with restraint.</strong> Four uses. No stock. Falls back gracefully.</li>
      <li><strong>Two save actions, unambiguous.</strong> Navy pill vs brass ghost, different confirmations.</li>
      <li><strong>Visibility from day one.</strong> Private · Team · Firm. Inline chip, never modal.</li>
      <li><strong>Cross-surface without duplication.</strong> Journal entries live once, in the roadmap. Client and Advisory Desk surfaces are read-through projections.</li>
      <li><strong>Progressive disclosure.</strong> Prompts collapsed. Layered context hidden. Guided mode opt-in. History behind "See history."</li>
      <li><strong>Institutional credibility.</strong> Playfair display serif and mono chrome give the surface the seriousness of a printed monograph. Not New York Times imitation — YadidOS through an editorial lens.</li>
      <li><strong>Accessible by construction.</strong> Semantic article structure, ARIA landmarks, keyboard focus preserved, Esc closes to map, reduced-motion respected.</li>
    </ul>

    <p style="margin-top: 30px;"><strong>Suggested build order</strong> · <br>
      <strong>Phase A</strong> — EmbraceMasthead · ClientMonogramCard · AtmospherePanel · MapCanvas · HeadlineBlock · CurrentReflectionArticle · WorkingJournalColumn · VisibilityChip · ContextRibbon. Backend: <code>reflection_history</code>, <code>visibility</code>, <code>status</code>, <code>promoted_at</code>, <code>source_note_id</code> on journal entries.<br>
      <strong>Phase B</strong> — PromptIndex progressive disclosure · Guided Reflection opt-in overlay.<br>
      <strong>Phase C</strong> — LayeredContextIndex with "+ N more" drawers · mobile category drawers.<br>
      <strong>Phase D</strong> — Imagery upload flow (client atmosphere + reading plates + real avatars) · reduced-motion polish · Firm-visibility partner filter.
    </p>
  </section>

  <div class="footer-note">Yadid Advisory · YadidOS · Volume IV · The Embrace Instrument · Round 4 · Feb 2026 · Visual direction only — no production code changed. Palette: Ivory · Navy Ink · Stone · Black · restrained Brass · Typography: Playfair Display · Cormorant Garamond · Merriweather · Inter · IBM Plex Mono.</div>
</div>
</body>
</html>
`;
